import React from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { permissionService } from "@/services/permissionService";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
  featureFlag?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
  featureFlag,
}) => {
  const {
    user,
    selectedContext,
    activeRole,
    hasPermission,
    isInitializing,
    isSelectingContext,
  } = useAuth();
  const { t } = useTranslation();

  // console.log('🛡️ ProtectedRoute render:', {
  //   path: window.location.pathname,
  //   hasUser: !!user,
  //   selectedContext,
  //   activeRole,
  //   isInitializing,
  //   isSelectingContext,
  //   requiredPermission
  // });

  // Step 1: Show loading ONLY during actual initialization
  if (isInitializing) {
    console.log("⏳ ProtectedRoute: Initializing authentication...");
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        <div
          style={{
            width: "60px",
            height: "60px",
            border: "6px solid rgba(255, 255, 255, 0.3)",
            borderTop: "6px solid white",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            marginBottom: "24px",
          }}
        />
        <h2 style={{ fontSize: "24px", fontWeight: "600", marginBottom: "8px" }}>
          {t('loading.loadingAccount')}
        </h2>
        <p style={{ fontSize: "16px", opacity: 0.9 }}>
          {t('loading.initializingAuth')}
        </p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Step 2: If no user, redirect to login immediately
  if (!user && !isSelectingContext) {
    console.log("🔒 ProtectedRoute: No user, redirecting to login");
    return <Navigate to="/login" replace />;
  }

  // Step 3: If user exists but no active role, show loading (waiting for role selection)
  if (user && !activeRole && !isSelectingContext) {
    console.log("⏳ ProtectedRoute: User authenticated, waiting for role selection...");
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
        }}
      >
        <div
          style={{
            width: "60px",
            height: "60px",
            border: "6px solid rgba(255, 255, 255, 0.3)",
            borderTop: "6px solid white",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            marginBottom: "24px",
          }}
        />
        <h2 style={{ fontSize: "24px", fontWeight: "600", marginBottom: "8px" }}>
          {t('loading.loadingAccount')}
        </h2>
        <p style={{ fontSize: "16px", opacity: 0.9 }}>
          {t('loading.settingUpWorkspace')}
        </p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // If currently in context selection flow, allow navigation
  if (isSelectingContext) {
    console.log(
      "🔄 ProtectedRoute: Context selection in progress, allowing navigation",
    );
    return <>{children}</>;
  }

  // If user has multiple contexts but hasn't selected one yet
  if (
    user.contexts &&
    user.contexts.length > 1 &&
    !selectedContext
  ) {
    console.log(
      "🔒 ProtectedRoute: Multiple contexts, no selection, redirecting to context selection",
    );
    return <Navigate to="/context-selection" replace />;
  }

  // If feature flag required, check it
  if (featureFlag && !permissionService.isFeatureEnabled(featureFlag)) {
    return <Navigate to="/dashboard" replace />;
  }

  // If permission required, check it
  if (requiredPermission) {
    const hasRequiredPermission = hasPermission(requiredPermission);
    console.log("🔒 ProtectedRoute: Permission check", {
      required: requiredPermission,
      hasPermission: hasRequiredPermission,
      activeRole: activeRole, // activeRole is a string like 'OWNER', not an object
      selectedContext,
      user: user?.name,
    });

    if (!hasRequiredPermission) {
      console.warn(
        "❌ ProtectedRoute: Permission denied, redirecting to /unauthorized",
      );
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};
